// https://www.npmjs.com/package/react-datepicker vs https://www.npmjs.com/package/react-multi-date-picker
// https://www.npmjs.com/package/react-datepicker has more support and gets updates frequently
import 'react-datepicker/dist/react-datepicker.css';
import '~/styles/react-datepicker-overrides.css';

import { forwardRef, Ref } from 'react';
import DatePicker from 'react-datepicker';
import { Grid, InputAdornment, TextField, TextFieldProps, useTheme, useMediaQuery } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';

interface MUITextFieldProps extends Omit<TextFieldProps, 'ref'> {
  onClick?: () => void;
  helperText?: string;
  error?: boolean | undefined;
  valueRequired?: boolean;
}

interface DateRange {
  from: Date | null;
  to?: Date | null;
}
interface DateRangeSelectorProps {
  dateRange: DateRange;
  fromLabel: string;
  toLabel: string;
  fromDateRequired?: boolean;
  toDateRequired?: boolean;
  singleDateMode?: boolean;
  fromHelperText?: Array<string>;
  toHelperText?: Array<string>;
  onFromDateChange: (date: Date | null) => void;
  onToDateChange: (date: Date | null) => void;
  fromMinDate?: Date | null;
  fromMaxDate?: Date;
  toMinDate?: Date;
  toMaxDate?: Date;
}

const MUITextField = forwardRef(function MUITextField(
  { error, helperText, label, onClick, value, valueRequired, ...rest }: MUITextFieldProps,
  ref: Ref<HTMLInputElement>,
) {
  return (
    <TextField
      ref={ref}
      error={helperText !== undefined && helperText.length > 0}
      helperText={helperText}
      label={label}
      onClick={onClick}
      placeholder={label?.toString()}
      required={valueRequired}
      value={value}
      {...rest}
      sx={{
        backgroundColor: 'white',
        width: '100%',
      }}
      autoComplete='new-password'
      InputProps={{
        autoComplete: 'new-password',
        startAdornment: (
          <InputAdornment position="start">
            <DateRangeIcon />
          </InputAdornment>
        ),
      }}
    />
  );
});

function DateRangeSelector({
  dateRange,
  fromDateRequired,
  fromHelperText,
  fromLabel,
  fromMaxDate,
  fromMinDate,
  onFromDateChange,
  onToDateChange,
  singleDateMode,
  toDateRequired,
  toHelperText,
  toLabel,
  toMaxDate,
  toMinDate,
}: DateRangeSelectorProps) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));  // Detect mobile devices


  return (
    <Grid container spacing={2}>
      <Grid item xs={singleDateMode || isSmall ? 12 : 6}>
        <DatePicker
          className="from-date"
          customInput={
            <MUITextField
              helperText={fromHelperText?.join('')}
              label={fromLabel}
              valueRequired={fromDateRequired}
              sx={{ backgroundColor: 'transparent !important' }}
            />
          }
          endDate={dateRange.to}
          minDate={fromMinDate === null ? undefined : fromMinDate ?? new Date()}
          maxDate={fromMaxDate}
          monthsShown={2}
          onChange={date => onFromDateChange(date)}
          selected={dateRange.from}
          selectsStart
          startDate={dateRange.from}
          autoComplete='new-password'
          popperPlacement={'bottom'}
          popperModifiers={isSmall ? [
            {
              name: 'bottom',
              fn: state => {
                state.x = 0;
                state.y = 0;
                return state;
              }
            },
          ] : []}
        />
      </Grid>
      {!singleDateMode ? (
        <Grid item xs={isSmall ? 12 : 6} className="to-date-grid">
          <DatePicker
            className="to-date"
            customInput={
              <MUITextField
                helperText={toHelperText?.join('')}
                label={toLabel}
                valueRequired={toDateRequired}
                sx={{ backgroundColor: 'transparent !important' }}
              />
            }
            endDate={dateRange.to}
            minDate={toMinDate ?? dateRange.from ?? undefined}
            maxDate={toMaxDate}
            monthsShown={2}
            onChange={date => onToDateChange(date)}
            selected={dateRange.to}
            selectsEnd
            startDate={dateRange.from}
            autoComplete='new-password'
            popperPlacement='bottom'
            popperModifiers={isSmall ? [
              {
                name: 'bottom',
                fn: state => {
                  state.x = 0;
                  state.y = 0;
                  return state;
                }
              },
            ] : []}
          />
        </Grid>
      ) : null}
    </Grid>
  );
}

export default DateRangeSelector;
